/**
 * Map worker agent_directory.payload JSON → AgentTile row (cached / offline view).
 */
import type { AgentStatusRow } from '../components/AgentTile';

type UcSnapshot = {
  agent_id?: number;
  state?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  pause_reason?: string | null;
  campaigns?: unknown;
};

function normalizeCampaignCodes(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const c of raw) {
    if (typeof c !== 'string') continue;
    const t = c.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

function campaignLabel(codes: string[]): string {
  if (codes.length === 0) return '';
  const max = 3;
  const head = codes.slice(0, max).join(', ');
  return codes.length > max ? `${head}…` : head;
}

export type AgentDirectoryPayload = {
  uc: UcSnapshot;
  resolvedName?: string;
  resolvedTeam?: string;
  appUserId?: string | null;
  /** Seconds in current ipSCAPE state (+ pause_reason), written on full UC sync only. */
  statusDurationSec?: number;
};

export function parseAgentDirectoryPayload(
  agentId: number,
  payloadJson: string,
  isLoggedInFromPlatform?: boolean | null
): AgentStatusRow | null {
  let o: AgentDirectoryPayload;
  try {
    o = JSON.parse(payloadJson) as AgentDirectoryPayload;
  } catch {
    return null;
  }
  const uc = o.uc ?? {};
  const codes = normalizeCampaignCodes(uc.campaigns);
  const fn = (uc.first_name ?? '').trim();
  const ln = (uc.last_name ?? '').trim();
  const em = (uc.email ?? '').trim();
  const name =
    o.resolvedName?.trim() ||
    ([fn, ln].filter(Boolean).join(' ') || em || `Agent ${agentId}`);
  const id = o.appUserId && String(o.appUserId).trim() ? String(o.appUserId) : `uc-${agentId}`;
  const state = (uc.state ?? 'unknown').trim() || 'unknown';

  let statusDuration = 0;
  if (typeof o.statusDurationSec === 'number' && Number.isFinite(o.statusDurationSec)) {
    statusDuration = Math.max(0, Math.floor(o.statusDurationSec));
  }

  return {
    id,
    name,
    email: em || undefined,
    team: o.resolvedTeam ?? '',
    campaign: campaignLabel(codes),
    campaign_codes: codes.length ? codes : undefined,
    pause_reason: uc.pause_reason ?? null,
    ipscapeStatus: state,
    wildixStatus: 'unknown',
    statusDuration,
    avatar_url: null,
    isLoggedIn: isLoggedInFromPlatform === true,
  };
}
